
// CSV profile persistence. Deliberately its OWN small localStorage-backed
// store, not shoehorned into DigitizationSettingsStore (settings/
// DigitizationSettings.js): that store's whole design is a FIXED schema of
// named groups/keys (SettingsSchema.js's buildDefaults()), merged
// key-by-key against known defaults - CSV profiles are a variable-length,
// user-authored ARRAY of arbitrarily-shaped records (columns can be added/
// removed/reordered freely), which doesn't fit that merge model at all.
// Reuses the same underlying PATTERN instead (task: "Byg ikke en ny backend
// eller projekt-database... Hvis eksisterende browserbaseret settings
// storage er den korrekte løsning, anvend den og dokumentér begrænsningen"):
// one versioned JSON blob in localStorage, in-memory Map fallback when
// localStorage is unavailable, never throws.
//
// Limitation (documented per the task's own instruction): this is
// per-BROWSER, per-origin storage - profiles do not sync across machines/
// browsers/users, and clearing site data removes them. No server-side
// profile library exists (explicitly out of scope this phase).
import {createStandardProfile, cloneProfile} from "./CsvProfile.js";

import {t} from "../../i18n/index.js";
const STORAGE_KEY = "potree.digitization.csvProfiles";
// Version 2 (2026-10): Standard PointView har fået ny definition (ID, X, Y, Z, Name, Vertex). Et lager i version 1 har den
// gamle standardprofil liggende, så den erstattes ved indlæsning; brugerens egne profiler røres ikke.
const STORAGE_VERSION = 2;

function createDefaultStorageBackend(){
	if(typeof localStorage !== "undefined"){
		return localStorage;
	}
	const memory = new Map();
	return {
		getItem: (key) => (memory.has(key) ? memory.get(key) : null),
		setItem: (key, value) => { memory.set(key, String(value)); },
		removeItem: (key) => { memory.delete(key); },
	};
}

export class CsvProfileStore {
	constructor(storageBackend = createDefaultStorageBackend()){
		this._storage = storageBackend;
		this._migrated = false;
		this._profiles = this._loadFromStorage();
		if(this._profiles.length === 0) this._profiles = [createStandardProfile()];
		if(this._migrated) this._saveToStorage();
	}

	list(){
		return this._profiles.slice();
	}

	get(id){
		return this._profiles.find((p) => p.id === id) || null;
	}

	getDefault(){
		return this.get("standard") || this._profiles[0];
	}

	// Creates the profile if its id is new, otherwise overwrites the
	// existing one in place (same position) - a single save() covers both
	// "opret" and "redigér" (task).
	save(profile){
		const index = this._profiles.findIndex((p) => p.id === profile.id);
		if(index === -1) this._profiles.push(profile);
		else this._profiles[index] = profile;
		this._saveToStorage();
		return profile;
	}

	rename(id, newName){
		const profile = this.get(id);
		if(!profile) return null;
		profile.name = newName;
		this._saveToStorage();
		return profile;
	}

	duplicate(id, newName){
		const profile = this.get(id);
		if(!profile) return null;
		const copy = cloneProfile(profile, undefined, newName || t("{0} (kopi)", profile.name));
		this._profiles.push(copy);
		this._saveToStorage();
		return copy;
	}

	// Refuses to delete the last remaining profile - a deliberate, minimal
	// safeguard (not in the task's explicit requirements, but "slettes" was
	// never meant to be able to leave the export UI with zero selectable
	// profiles) rather than silently reseeding a fresh standard profile
	// behind the user's back.
	delete(id){
		if(this._profiles.length <= 1) return false;
		const index = this._profiles.findIndex((p) => p.id === id);
		if(index === -1) return false;
		this._profiles.splice(index, 1);
		this._saveToStorage();
		return true;
	}

	_loadFromStorage(){
		try{
			const raw = this._storage.getItem(STORAGE_KEY);
			if(!raw) return [];
			const parsed = JSON.parse(raw);
			if(!parsed || typeof parsed !== "object" || !Array.isArray(parsed.profiles)) return [];
			if((parsed.version || 1) < STORAGE_VERSION){
				const migrated = parsed.profiles.map((p) => (p && p.id === "standard" ? createStandardProfile() : p));
				this._migrated = true;
				return migrated;
			}
			return parsed.profiles;
		}catch(e){
			return [];
		}
	}

	_saveToStorage(){
		try{
			this._storage.setItem(STORAGE_KEY, JSON.stringify({version: STORAGE_VERSION, profiles: this._profiles}));
		}catch(e){
			// quota exceeded / storage disabled - profiles just won't persist
		}
	}
}

export const csvProfileStore = new CsvProfileStore();
